import { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { getLatestWeather, getWeatherHistory } from "../../services/weatherApi";
import "./Weather.css";

const METRICS = [
  { key: "temperature", label: "Temperature", unit: "°C", color: "#f59e0b" },
  { key: "humidity", label: "Humidity", unit: "%", color: "#3b82f6" },
  { key: "pressure", label: "Pressure", unit: "hPa", color: "#8b5cf6" },
];

function formatRelativeTime(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatXAxis(ts) {
  const d = new Date(ts);
  return `${d.getDate()}/${d.getMonth() + 1} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

function getDefaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

function getDefaultTo() {
  return new Date().toISOString().slice(0, 10) + "T23:59:59Z";
}

function CustomTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="weather-tooltip">
      <div className="weather-tooltip-label">
        {new Date(label).toLocaleString()}
      </div>
      <div className="weather-tooltip-value">
        {payload[0].value?.toFixed(1)} {unit}
      </div>
    </div>
  );
}

export default function Weather() {
  const [latest, setLatest] = useState(null);
  const [sensorStatus, setSensorStatus] = useState("unknown");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ranges, setRanges] = useState(() =>
    Object.fromEntries(
      METRICS.map((m) => [m.key, { from: getDefaultFrom(), to: getDefaultTo() }])
    )
  );

  const fetchLatest = useCallback(async () => {
    try {
      const data = await getLatestWeather();
      setSensorStatus(data.status || "unknown");
      setLatest(data.reading || data);
    } catch (err) {
      console.error("Failed to fetch latest weather:", err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const from = ranges.temperature.from || getDefaultFrom();
      const to = ranges.temperature.to || getDefaultTo();
      const data = await getWeatherHistory("living_room", from, to);
      setHistory(data.readings || data || []);
    } catch (err) {
      console.error("Failed to fetch weather history:", err);
      setHistory([]);
    }
  }, [ranges]);

  useEffect(() => {
    async function init() {
      setLoading(true);
      await Promise.all([fetchLatest(), fetchHistory()]);
      setLoading(false);
    }
    init();
  }, [fetchLatest, fetchHistory]);

  // Refetch when page becomes visible (tab switch / navigation)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchLatest();
        fetchHistory();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchLatest, fetchHistory]);

  // Auto-refresh latest every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchLatest, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLatest]);

  const handleRangeChange = (metric, field, value) => {
    setRanges((prev) => ({
      ...prev,
      [metric]: { ...prev[metric], [field]: value },
    }));
  };

  if (loading) {
    return (
      <div className="weather-page">
        <h1>Weather</h1>
        <div className="weather-loading">Loading...</div>
      </div>
    );
  }

  const hasData = latest && (latest.temperature !== undefined || latest.humidity !== undefined || latest.pressure !== undefined);

  return (
    <div className="weather-page">
      <h1>Weather</h1>

      {sensorStatus === "offline" && (
        <div className="weather-offline-banner">
          <span>⚠️</span> Sensor offline — last reading was {formatRelativeTime(latest?.timestamp)}
        </div>
      )}

      {!hasData ? (
        <div className="weather-empty">
          <div className="weather-empty-icon">🌡️</div>
          <p>No readings yet</p>
          <p style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
            Weather data will appear here once sensors start reporting.
          </p>
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="weather-cards">
            {METRICS.map((m) => (
              <div key={m.key} className={`weather-card weather-card--${m.key}`}>
                <div className="weather-card-value">
                  {latest[m.key] !== undefined ? latest[m.key].toFixed(1) : "—"}
                  <span className="weather-card-unit">{m.unit}</span>
                </div>
                <div className="weather-card-label">{m.label}</div>
                <div className="weather-card-timestamp">
                  {formatRelativeTime(latest.timestamp)}
                </div>
              </div>
            ))}
          </div>

          {/* Graphs */}
          <div className="weather-graphs">
            {METRICS.map((m) => (
              <div key={m.key} className="weather-graph-section">
                <div className="weather-graph-header">
                  <span className="weather-graph-title">
                    {m.label} ({m.unit})
                  </span>
                  <div className="weather-graph-dates">
                    <label>From</label>
                    <input
                      type="date"
                      value={ranges[m.key].from}
                      onChange={(e) => handleRangeChange(m.key, "from", e.target.value)}
                    />
                    <label>To</label>
                    <input
                      type="date"
                      value={ranges[m.key].to}
                      onChange={(e) => handleRangeChange(m.key, "to", e.target.value)}
                    />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={history} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatXAxis}
                      tick={{ fontSize: 11, fill: "var(--color-text-muted)" }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--color-text-muted)" }}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip content={<CustomTooltip unit={m.unit} />} />
                    <Line
                      type="monotone"
                      dataKey={m.key}
                      stroke={m.color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
